'use client';

const PAGE_BUTTONS_TO_SHOW = 3;

export const CustomPagination = ({
  currentPage,
  totalPages,
  pagesToShow = PAGE_BUTTONS_TO_SHOW,
  onPageChange,
}) => {
  const handlePrev = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const pageNumbers = Array.from(
    { length: totalPages },
    (_, index) => index + 1
  );

  let leftOffset = Math.floor(pagesToShow / 2);
  let rightOffset = pagesToShow % 2 === 0 ? leftOffset - 1 : leftOffset;

  let buttonsStartIndex = currentPage - leftOffset;
  let buttonsEndIndex = currentPage + rightOffset;

  if (buttonsStartIndex < 1) {
    buttonsStartIndex = 1;
    buttonsEndIndex = Math.min(totalPages, pagesToShow);
  }
  if (buttonsEndIndex > totalPages) {
    buttonsEndIndex = totalPages;
    buttonsStartIndex = Math.max(1, totalPages - pagesToShow + 1);
  }

  const visiblePageNumbers = pageNumbers.slice(
    buttonsStartIndex - 1,
    buttonsEndIndex
  );

  return (
    <div className="flex justify-center items-center mx-auto px-8 py-[14px] border border-[#0C3438] rounded-full w-fit">
      <nav
        className="flex items-center gap-x-4 font-inter-tight"
        aria-label="Pagination"
      >
        {/* Previous button */}
        <button
          type="button"
          className="inline-flex items-center gap-x-2 disabled:opacity-40 focus:outline-hidden text-[#999999] text-lg -rotate-180 disabled:pointer-events-none jusify-center"
          aria-label="Previous"
          disabled={currentPage === 1}
          onClick={handlePrev}
        >
          <Arrow />
          <span className="sr-only">Previous</span>
        </button>

        {/* Pages */}
        <div className={`flex justify-evenly items-center w-fit`}>
          {/* First page button (if needed) */}
          {visiblePageNumbers[0] > 1 && (
            <>
              <button
                type="button"
                onClick={() => onPageChange(1)}
                className={`flex justify-center items-center px-2 rounded-lg focus:outline-hidden text-lg ${
                  currentPage === 1 ? 'text-white' : 'text-[#999999]'
                }`}
              >
                1
              </button>

              {/* Left dots if gap > 1 */}
              {visiblePageNumbers[0] > 2 && (
                <span className="px-1 text-[#999999] text-lg select-none">
                  ...
                </span>
              )}
            </>
          )}

          {/* Middle visible pages */}
          {visiblePageNumbers.map((page) => {
            return (
              <button
                key={page}
                type="button"
                className={`flex justify-center items-center px-2 rounded-lg focus:outline-hidden text-lg disabled:pointer-events-none ${
                  currentPage === page ? 'text-white' : 'text-[#999999] '
                }`}
                aria-current="page"
                onClick={() => onPageChange(page)}
                disabled={currentPage === page}
              >
                {page}
              </button>
            );
          })}

          {/* Right dots if gap > 1 */}
          {visiblePageNumbers[visiblePageNumbers.length - 1] < totalPages && (
            <>
              {visiblePageNumbers[visiblePageNumbers.length - 1] <
                totalPages - 1 && (
                <span className="px-1 text-[#999999] text-lg select-none">
                  ...
                </span>
              )}
              <button
                type="button"
                onClick={() => onPageChange(totalPages)}
                className={`flex justify-center items-center px-2 rounded-lg focus:outline-hidden text-lg ${
                  currentPage === totalPages ? 'text-white' : 'text-[#999999]'
                }`}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        {/* Next button */}
        <button
          type="button"
          className="inline-flex items-center gap-x-2 disabled:opacity-50 focus:outline-hidden text-[#999999] text-lg disabled:pointer-events-none jusify-center"
          aria-label="Next"
          onClick={handleNext}
          disabled={currentPage === totalPages}
        >
          <span className="sr-only">Next</span>
          <Arrow />
        </button>
      </nav>
    </div>
  );
};

const Arrow = () => {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.3536 11.3536C19.5488 11.1583 19.5488 10.8417 19.3536 10.6464L16.1716 7.46447C15.9763 7.2692 15.6597 7.2692 15.4645 7.46447C15.2692 7.65973 15.2692 7.97631 15.4645 8.17157L18.2929 11L15.4645 13.8284C15.2692 14.0237 15.2692 14.3403 15.4645 14.5355C15.6597 14.7308 15.9763 14.7308 16.1716 14.5355L19.3536 11.3536ZM2 11V11.5L19 11.5V11V10.5L2 10.5L2 11Z"
        fill="currentColor"
      />
    </svg>
  );
};
